import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useBulkJobStatus } from "@/features/leads/api/useBulkJobStatus";
import { invalidateTrackQueries } from "@/features/stage/api/invalidateTrackQueries";
import {
  queryClient,
  invalidateLeadBoard,
  invalidateLeadCaches,
  invalidateLeadAssignment,
  invalidateLeadDetail,
  invalidateLeadTimeline,
  invalidateRecentActivityFeed,
} from "@/lib/queryClient";

/**
 * Apple-Dynamic-Island-style progress pills for bulk operations.
 *
 * Rendered by <BulkPillHost/> (mounted once in AppProvider) — NOT by sonner —
 * so multiple simultaneous bulks display SIDE BY SIDE in a top-center row
 * instead of stacking behind each other.
 *
 * - Persists across route changes (host lives at the app root).
 * - Shown only to the user who triggered the bulk (fired client-side).
 * - Self-dismisses a few seconds after reaching a terminal state.
 *
 * Two flavours:
 *   1. Job-backed (async worker bulks): `showBulkToast(jobId, meta)` — polls
 *      the job and owns the post-completion cache refresh.
 *   2. Sync (single-request bulks, e.g. bulk unassign): `showSyncBulkPill(meta)`
 *      returns a handle — call `.complete()` / `.fail()` when the request
 *      settles. `showCompletedBulkPill(meta, …)` pops one directly in the
 *      done state for sync branches that finish with the response.
 */

export type BulkOperationKind =
  | "stage"
  | "fields"
  | "archive"
  | "restore"
  | "trash"
  | "branch-transfer"
  | "import"
  | "assign"
  | "unassign"
  | "tracks"
  | "automation";

interface BulkToastMeta {
  kind: BulkOperationKind;
  total: number;
  /** Noun for the terminal label — defaults to "leads" (e.g. "rows"). */
  noun?: string;
}

const VERB: Record<BulkOperationKind, { running: string; done: string }> = {
  stage: { running: "Updating", done: "Updated" },
  fields: { running: "Updating", done: "Updated" },
  archive: { running: "Archiving", done: "Archived" },
  restore: { running: "Restoring", done: "Restored" },
  trash: { running: "Deleting", done: "Deleted" },
  "branch-transfer": { running: "Transferring", done: "Transferred" },
  import: { running: "Importing", done: "Imported" },
  assign: { running: "Assigning", done: "Assigned" },
  unassign: { running: "Unassigning", done: "Unassigned" },
  tracks: { running: "Updating tracks on", done: "Updated tracks on" },
  automation: { running: "Running automation on", done: "Ran automation on" },
};

const fmt = (n: number) => n.toLocaleString();

/* ───────────────────── outcome subtitle (shared) ─────────────────────
   A partial bulk MUST say why. "8 failed" on its own is the single most
   useless thing this pill can show — the server already returns a per-row
   `reason` on both `failed[]` and `skipped[]`, and until now only the
   async JobPill surfaced it while every sync branch dropped it on the
   floor. Both paths go through these helpers so they read identically. */

type OutcomeRow = { reason?: unknown };

/** The most frequent `reason` across rows ("" when none carry one). */
export function topReason(rows: OutcomeRow[] | undefined | null): string {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const counts = new Map<string, number>();
  for (const r of rows) {
    const reason = String(r?.reason ?? "").trim();
    if (!reason) continue;
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  let best = "";
  let bestCount = 0;
  for (const [reason, count] of counts) {
    if (count > bestCount) {
      best = reason;
      bestCount = count;
    }
  }
  return best;
}

/**
 * "<n> skipped · <n> failed · <most common reason>" for a bulk summary.
 * The reason prefers `failed` (an error the operator must act on) and falls
 * back to `skipped` (usually "not in the selected source stage", which is
 * exactly what a confused precise-mode bulk needs to say). `undefined` when
 * everything went through, so the caller shows its own "All done".
 */
export function bulkOutcomeSubtitle(result: {
  skipped?: OutcomeRow[] | null;
  failed?: OutcomeRow[] | null;
}): string | undefined {
  const skipped = Array.isArray(result?.skipped) ? result.skipped : [];
  const failed = Array.isArray(result?.failed) ? result.failed : [];
  if (skipped.length === 0 && failed.length === 0) return undefined;
  const reason = topReason(failed) || topReason(skipped);
  return (
    [
      skipped.length ? `${fmt(skipped.length)} skipped` : "",
      failed.length ? `${fmt(failed.length)} failed` : "",
      reason,
    ]
      .filter(Boolean)
      .join(" · ") || undefined
  );
}

// One-time keyframe injection (completion pop + indeterminate sweep + entry).
if (typeof document !== "undefined" && !document.getElementById("bulkpill-kf")) {
  const s = document.createElement("style");
  s.id = "bulkpill-kf";
  s.textContent =
    `@keyframes bulkpill-pop{0%{transform:scale(.4);opacity:0}60%{transform:scale(1.18)}100%{transform:scale(1);opacity:1}}` +
    `@keyframes bulkpill-in{0%{transform:translateY(-12px) scale(.92);opacity:0}100%{transform:translateY(0) scale(1);opacity:1}}` +
    `@keyframes bulkpill-indet{0%{transform:translateX(-110%)}100%{transform:translateX(320%)}}`;
  document.head.appendChild(s);
}

/* ─────────────────────────── pill store ───────────────────────────
   Tiny module-level pub/sub so any mutation can push a pill without a
   provider. The host subscribes via useSyncExternalStore. */

type SyncState = {
  status: "running" | "completed" | "failed";
  /** Terminal count for the "<Done> N <noun>" label. */
  done?: number;
  /** Terminal sub-line (e.g. "12 skipped · 3 failed"). */
  subtitle?: string;
  failedReason?: string;
};

type PillEntry =
  | { id: string; mode: "job"; jobId: string; meta: BulkToastMeta }
  | { id: string; mode: "sync"; meta: BulkToastMeta; state: SyncState }
  | {
      id: string;
      mode: "undo";
      meta: BulkToastMeta;
      /** Epoch ms at which the held action commits. */
      commitAt: number;
      undo: () => void;
      commitNow: () => void;
    };

const pillMap = new Map<string, PillEntry>();
let snapshot: PillEntry[] = [];
const listeners = new Set<() => void>();

function emit() {
  snapshot = Array.from(pillMap.values());
  listeners.forEach((l) => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}
function getSnapshot() {
  return snapshot;
}
function removePill(id: string) {
  if (pillMap.delete(id)) emit();
}

let syncSeq = 0;

/**
 * Fire a job-backed progress pill. No-op if jobId is missing (the op ran
 * synchronously below the server threshold — use `showCompletedBulkPill`
 * for that branch). Keyed by jobId so re-fires for the same job de-dupe.
 */
export function showBulkToast(
  jobId: string | null | undefined,
  meta: BulkToastMeta,
) {
  if (!jobId) return;
  const id = `bulk-${jobId}`;
  if (pillMap.has(id)) return;
  pillMap.set(id, { id, mode: "job", jobId, meta });
  emit();
}

export interface SyncPillHandle {
  complete(result?: { done?: number; subtitle?: string }): void;
  fail(reason?: string): void;
  dismiss(): void;
}

/**
 * Pill for a single-request bulk (no job to poll). Shows an indeterminate
 * sweep until `.complete()` / `.fail()` is called; cache invalidation stays
 * with the calling mutation.
 */
export function showSyncBulkPill(meta: BulkToastMeta): SyncPillHandle {
  const id = `bulk-sync-${++syncSeq}`;
  pillMap.set(id, {
    id,
    mode: "sync",
    meta,
    state: { status: "running" },
  });
  emit();

  const patch = (state: SyncState) => {
    const entry = pillMap.get(id);
    if (!entry || entry.mode !== "sync") return;
    pillMap.set(id, { ...entry, state });
    emit();
  };

  return {
    complete: (result) =>
      patch({
        status: "completed",
        done: result?.done ?? meta.total,
        subtitle: result?.subtitle,
      }),
    fail: (reason) => patch({ status: "failed", failedReason: reason }),
    dismiss: () => removePill(id),
  };
}

/** Pop a pill straight into the done state (sync branches that finish with the response). */
export function showCompletedBulkPill(
  meta: BulkToastMeta,
  result?: { done?: number; subtitle?: string },
) {
  showSyncBulkPill(meta).complete(result);
}

/* ─────────────────────────── undo window ───────────────────────────
   A major bulk (assign / unassign / stage / fields / archive / trash /
   branch transfer) is HELD for `UNDO_WINDOW_MS` before it is sent. Undo inside
   the window means the request never leaves the browser — the only undo that
   is honest for every kind: once the server commits, automations, follow-up
   re-dates, notifications and WhatsApp sends have already fired and cannot be
   recalled. The unit is one user CONFIRMATION (the whole `run`), not one
   mutation, so a multi-step bulk update gets a single window and keeps its
   internal ordering.

   The hold lives at module level (like the pills), so it survives route
   changes and modal unmounts. A tab close would drop it — `beforeunload`
   prompts while anything is held. */

export const UNDO_WINDOW_MS = 20_000;

const heldActions = new Set<string>();

function onBeforeUnload(e: BeforeUnloadEvent) {
  if (heldActions.size === 0) return;
  e.preventDefault();
  e.returnValue = "";
}
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", onBeforeUnload);
}

let undoSeq = 0;

/**
 * Hold `run` for the undo window, then fire it. Returns immediately — the
 * caller should close its dialog and clear the selection right away. `run`
 * owns its own progress pill / toasts / cache busts exactly as before.
 */
export function runWithUndoWindow(
  meta: BulkToastMeta,
  run: () => Promise<unknown> | unknown,
) {
  const id = `bulk-undo-${++undoSeq}`;
  let settled = false;

  const settle = (fire: boolean) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    heldActions.delete(id);
    removePill(id);
    if (!fire) return;
    // Mutations surface their own errors; never leave a rejection unhandled.
    Promise.resolve()
      .then(run)
      .catch(() => {});
  };

  const timer = setTimeout(() => settle(true), UNDO_WINDOW_MS);
  heldActions.add(id);
  pillMap.set(id, {
    id,
    mode: "undo",
    meta,
    commitAt: Date.now() + UNDO_WINDOW_MS,
    undo: () => settle(false),
    commitNow: () => settle(true),
  });
  emit();
}

/* ─────────────────────────── components ─────────────────────────── */

function PillShell({
  isTerminal,
  isDone,
  isFailed,
  accent,
  title,
  counter,
  pct,
  indeterminate,
  subtitle,
}: {
  isTerminal: boolean;
  isDone: boolean;
  isFailed: boolean;
  accent: string;
  title: string;
  counter?: string | null;
  pct: number;
  indeterminate?: boolean;
  subtitle?: string | null;
}) {
  return (
    <div
      className="pointer-events-auto flex min-w-[268px] max-w-[360px] select-none items-center gap-3 rounded-full bg-white/95 px-4 py-2.5 text-[#051912] shadow-xl ring-1 ring-[#e6e8e7] backdrop-blur"
      style={{ animation: "bulkpill-in 280ms cubic-bezier(.21,1.2,.55,1)" }}
    >
      {/* status indicator */}
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        {!isTerminal && (
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-[#e6e8e7]"
            style={{ borderTopColor: accent }}
          />
        )}
        {isTerminal && (
          <span
            className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold leading-none text-white"
            style={{ background: accent, animation: "bulkpill-pop 340ms cubic-bezier(.21,1.4,.6,1)" }}
          >
            {isDone ? "✓" : "!"}
          </span>
        )}
      </span>

      {/* label + progress */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[13px] font-medium leading-none">
            {title}
          </span>
          {!isTerminal && counter && (
            <span className="shrink-0 font-mono text-[12px] tabular-nums text-[#697571]">
              {counter}
            </span>
          )}
        </div>

        {!isTerminal ? (
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-[#eef0ef]">
            {indeterminate ? (
              <div
                className="absolute inset-y-0 w-1/3 rounded-full"
                style={{
                  background: accent,
                  animation: "bulkpill-indet 1.1s ease-in-out infinite",
                }}
              />
            ) : (
              <div
                className="h-full rounded-full transition-[width] duration-500 ease-out"
                style={{ width: `${pct}%`, background: accent }}
              />
            )}
          </div>
        ) : (
          // Two lines, not `truncate`: the subtitle now carries the failure
          // REASON, and a one-line ellipsis cut it off after a few words —
          // which is how "60 failed" stayed unexplained. `title` gives the
          // full text on hover for the rare long one.
          <span
            className="line-clamp-2 text-[11px] leading-snug text-[#697571]"
            title={subtitle || undefined}
          >
            {subtitle || (isFailed ? "Something went wrong." : "All done")}
          </span>
        )}
      </div>
    </div>
  );
}

function JobPill({ entry }: { entry: Extract<PillEntry, { mode: "job" }> }) {
  const { jobId, meta, id } = entry;
  const { data } = useBulkJobStatus(jobId);
  const verb = VERB[meta.kind] ?? VERB.stage;
  const noun = meta.noun ?? "leads";

  const total = data?.progress?.total || meta.total || 0;
  const done = data?.progress?.done || 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const isDone = data?.status === "completed";
  const isFailed = data?.status === "failed";
  const isTerminal = isDone || isFailed;

  const summary = data?.summary;
  const succeeded = summary?.success?.length ?? (isDone ? total : done);

  // The pill outlives route changes, so it (not the page that fired the
  // bulk) owns the post-completion cache refresh.
  const dismissedRef = useRef(false);
  useEffect(() => {
    if (!isTerminal || dismissedRef.current) return;
    dismissedRef.current = true;
    if (meta.kind === "branch-transfer" || meta.kind === "automation") {
      // Branch transfer moves every scoped view — the full bust is its
      // reserved use (see lib/queryClient.tsx). An assignment automation can
      // move the branch as well as the people, so it takes the same bust.
      invalidateLeadCaches();
      // Branch Mismatch panel (transfer-to-user-branch launches these too).
      queryClient.invalidateQueries({ queryKey: ["branch-mismatch-leads"] });
    } else if (meta.kind === "assign" || meta.kind === "unassign") {
      // Assignment state also feeds the assign-eligibility hook.
      invalidateLeadAssignment();
    } else {
      invalidateLeadBoard();
    }
    if (meta.kind === "stage") {
      // Bulk stage moves write activity rows and can move deals (precise /
      // deal-only) — same busts as the sync branch.
      invalidateLeadTimeline();
      invalidateLeadDetail();
    } else {
      // The other bulks write feed rows too; stage gets it via the timeline.
      invalidateRecentActivityFeed();
    }
    if (meta.kind === "tracks") {
      // Track writes open / close track follow-ups and move the dashboard's
      // track cards; every open drawer's Tracks tab reads entity-tracks.
      invalidateTrackQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["entity-tracks"] });
      invalidateLeadTimeline();
    }
    if (meta.kind === "trash") {
      queryClient.invalidateQueries({ queryKey: ["trashed-leads"] });
      // Large trashes launched from the Archived panel (DeleteConfirmationModal)
      // go async; its synchronous invalidate fires before the worker actually
      // trashes the rows, so bust ["archived-leads"] here on completion too so
      // the just-trashed rows drop out. Harmless when trashing from the grid.
      queryClient.invalidateQueries({ queryKey: ["archived-leads"] });
    }
    if (meta.kind === "archive" || meta.kind === "restore") {
      // The archive panel is keyed ["archived-leads"] — without this an
      // open archive view stayed stale after an ASYNC archive/restore
      // (the sync branch already busts it via invalidateArchiveAndLeads).
      queryClient.invalidateQueries({ queryKey: ["archived-leads"] });
    }
    const t = setTimeout(() => removePill(id), isFailed ? 6000 : 3500);
    return () => clearTimeout(t);
  }, [isTerminal, isFailed, id, meta.kind]);

  const accent = isFailed ? "#da2a46" : isDone ? "#156548" : "#335cff";

  return (
    <PillShell
      isTerminal={isTerminal}
      isDone={isDone}
      isFailed={isFailed}
      accent={accent}
      title={
        isDone
          ? `${verb.done} ${fmt(succeeded)} ${succeeded === 1 ? noun.replace(/s$/, "") : noun}`
          : isFailed
            ? "Bulk action failed"
            : `${verb.running} ${noun}…`
      }
      counter={`${fmt(done)}/${fmt(total)}`}
      pct={pct}
      subtitle={
        isFailed
          ? data?.failedReason ||
            "The job didn't finish — nothing was lost, retry it."
          : bulkOutcomeSubtitle({
              skipped: summary?.skipped,
              failed: summary?.failed,
            }) || "All done"
      }
    />
  );
}

function SyncPill({ entry }: { entry: Extract<PillEntry, { mode: "sync" }> }) {
  const { meta, state, id } = entry;
  const verb = VERB[meta.kind] ?? VERB.stage;
  const noun = meta.noun ?? "leads";

  const isDone = state.status === "completed";
  const isFailed = state.status === "failed";
  const isTerminal = isDone || isFailed;

  useEffect(() => {
    if (!isTerminal) return;
    const t = setTimeout(() => removePill(id), isFailed ? 6000 : 3500);
    return () => clearTimeout(t);
  }, [isTerminal, isFailed, id]);

  const accent = isFailed ? "#da2a46" : isDone ? "#156548" : "#335cff";
  const done = state.done ?? meta.total;

  return (
    <PillShell
      isTerminal={isTerminal}
      isDone={isDone}
      isFailed={isFailed}
      accent={accent}
      title={
        isDone
          ? `${verb.done} ${fmt(done)} ${done === 1 ? noun.replace(/s$/, "") : noun}`
          : isFailed
            ? "Bulk action failed"
            : `${verb.running} ${noun}…`
      }
      counter={meta.total > 0 ? fmt(meta.total) : null}
      pct={0}
      indeterminate
      subtitle={isFailed ? state.failedReason : state.subtitle}
    />
  );
}

function UndoPill({ entry }: { entry: Extract<PillEntry, { mode: "undo" }> }) {
  const { meta, commitAt, undo, commitNow } = entry;
  const verb = VERB[meta.kind] ?? VERB.stage;
  const noun = meta.noun ?? "leads";
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  const remainingMs = Math.max(0, commitAt - now);
  const seconds = Math.ceil(remainingMs / 1000);
  const pct = Math.min(100, (remainingMs / UNDO_WINDOW_MS) * 100);
  const label =
    meta.total > 0
      ? `${fmt(meta.total)} ${meta.total === 1 ? noun.replace(/s$/, "") : noun}`
      : noun;

  return (
    <div
      className="pointer-events-auto flex min-w-[320px] max-w-[420px] select-none items-center gap-3 rounded-full bg-white/95 py-2 pl-4 pr-2 text-[#051912] shadow-xl ring-1 ring-[#e6e8e7] backdrop-blur"
      style={{ animation: "bulkpill-in 280ms cubic-bezier(.21,1.2,.55,1)" }}
    >
      <span className="w-6 shrink-0 text-center font-mono text-[12px] tabular-nums text-[#697571]">
        {seconds}s
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="truncate text-[13px] font-medium leading-none">
          {verb.running} {label}
        </span>
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-[#eef0ef]">
          <div
            className="h-full rounded-full bg-[#335cff] transition-[width] duration-300 ease-linear"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={commitNow}
        title="Skip the wait and apply it now"
        className="shrink-0 cursor-pointer rounded-full px-2 py-1.5 text-[12px] font-medium text-[#697571] hover:bg-[#f2f4f3]"
      >
        Do now
      </button>
      <button
        type="button"
        onClick={undo}
        title="Cancel — nothing has been changed yet"
        className="shrink-0 cursor-pointer rounded-full bg-[#051912] px-3.5 py-1.5 text-[12px] font-medium text-white hover:bg-[#15261f]"
      >
        Undo
      </button>
    </div>
  );
}

/**
 * Mounted ONCE in AppProvider. Lays active pills out side by side
 * (wrapping on narrow screens) at the top-center of the viewport.
 */
export function BulkPillHost() {
  const pills = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (pills.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-[200] flex flex-wrap items-start justify-center gap-2 px-4">
      {pills.map((p) =>
        p.mode === "job" ? (
          <JobPill key={p.id} entry={p} />
        ) : p.mode === "undo" ? (
          <UndoPill key={p.id} entry={p} />
        ) : (
          <SyncPill key={p.id} entry={p} />
        ),
      )}
    </div>
  );
}
